import { Link } from "react-router-dom";
import { MapPin, Star, Clock, Coins } from "lucide-react";
import { thumbUrl } from "@/lib/api";

const conditionColor = {
  "Like New": "bg-teal-100 text-teal-800",
  "Good": "bg-amber-100 text-amber-800",
  "Fair": "bg-rose-100 text-rose-800",
};

export default function BookCard({ book }) {
  const cover = book.cover_urls?.[0];
  const km = typeof book.distance_km === "number" ? book.distance_km : null;
  return (
    <div
      className="card-soft group block overflow-hidden hover:-translate-y-1 transition-transform"
      data-testid={`book-card-${book.book_id}`}
    >
      <Link to={`/book/${book.book_id}`} className="block">
        <div className="aspect-[3/4] bg-amber-100 relative overflow-hidden flex items-center justify-center">
          {cover ? (
            <img
              src={thumbUrl(cover)}
              alt={book.title}
              className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-amber-400 font-display text-4xl">📚</div>
          )}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1">
            <span className={`badge-pill ${conditionColor[book.condition] || "bg-slate-100 text-slate-800"}`}>
              {book.condition}
            </span>
            {book.is_available === false && (
              <span className="badge-pill bg-slate-900/80 text-white">On Loan</span>
            )}
          </div>
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
            <span className="badge-pill bg-teal-500 text-white" data-testid={`book-free-badge-${book.book_id}`}>Free</span>
            {km !== null && (
              <span className="badge-pill bg-white/95 text-slate-700 shadow-sm" data-testid={`book-km-badge-${book.book_id}`}>
                <MapPin size={10} strokeWidth={2.4} /> {km.toFixed(1)} km away
              </span>
            )}
          </div>
        </div>
        <div className="p-4 space-y-2">
          <div>
            <h3 className="font-display font-semibold text-slate-900 leading-tight line-clamp-1">{book.title}</h3>
            <p className="text-sm text-slate-500 line-clamp-1">by {book.author}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="badge-pill bg-amber-100 text-amber-800">{book.age_group}</span>
            <span className="badge-pill bg-slate-100 text-slate-700">{book.genre}</span>
          </div>
          <div className="flex items-center justify-between pt-1 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <MapPin size={12} strokeWidth={2.4} />
              <span className="truncate max-w-[110px]">{book.Neighbourhood || "Nearby"}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={12} strokeWidth={2.4} />
              <span>up to {book.max_weeks}w</span>
            </div>
          </div>
          {book.owner && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              {book.owner.avatar_url ? (
                <img src={book.owner.avatar_url} className="w-6 h-6 rounded-full object-cover" alt="" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {book.owner.name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <span className="text-xs text-slate-600 truncate flex-1">{book.owner.name}</span>
              {book.owner.rating_count > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-amber-600">
                  <Star size={12} fill="currentColor" strokeWidth={0} />
                  {book.owner.rating_avg}
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="px-4 pb-4">
        <Link
          to={`/book/${book.book_id}?request=1`}
          className="btn-primary w-full justify-center text-sm py-2.5"
          data-testid={`book-request-${book.book_id}`}
          aria-disabled={book.is_available === false}
        >
          Request Now
          {/* Request <span className="inline-flex items-center gap-1 opacity-90 font-semibold"><Coins size={12} /> 2 credits</span> */}
        </Link>
      </div>
    </div>
  );
}
